import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, TruckIcon } from '@heroicons/react/24/outline';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { trucksAPI, clientsAPI } from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import SearchableSelect from '../components/SearchableSelect';
import Pagination from '../components/Pagination';
import LoadTicketLink from '../components/LoadTicketLink';
import { formatDate, formatDateTime } from '../utils/helpers';

const histLimit = 20;

const TruckDetailContent = ({ id }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isManagerOrAdmin, isPeseur } = useAuth();
  const showLoadHistory = isManagerOrAdmin() || isPeseur();

  const { data, loading, error } = useApi(() => trucksAPI.getById(id), [id]);
  const truck = data?.truck;

  const [activeTab, setActiveTab] = useState('info');
  const [histClientId, setHistClientId] = useState('');
  const [histStartDate, setHistStartDate] = useState('');
  const [histEndDate, setHistEndDate] = useState('');
  const [histPage, setHistPage] = useState(1);
  const [histLoading, setHistLoading] = useState(false);
  const [histLoads, setHistLoads] = useState([]);
  const [histPagination, setHistPagination] = useState({
    total: 0,
    page: 1,
    limit: histLimit,
    totalPages: 0
  });

  const { data: clientsData } = useApi(
    showLoadHistory
      ? () =>
          clientsAPI.getAll({
            limit: 200,
            ...(isPeseur() ? { allBilling: 'true' } : {})
          })
      : null,
    [showLoadHistory]
  );
  const clients = clientsData?.clients || clientsData?.data?.clients || [];

  useEffect(() => {
    setActiveTab('info');
    setHistPage(1);
    setHistClientId('');
    setHistStartDate('');
    setHistEndDate('');
  }, [id]);

  useEffect(() => {
    if (!showLoadHistory || activeTab !== 'history' || !truck?.id) return undefined;
    let cancelled = false;
    (async () => {
      setHistLoading(true);
      try {
        const res = await trucksAPI.getLoads(truck.id, {
          page: histPage,
          limit: histLimit,
          ...(histClientId ? { clientId: histClientId } : {}),
          ...(histStartDate ? { startDate: histStartDate } : {}),
          ...(histEndDate ? { endDate: histEndDate } : {})
        });
        if (cancelled) return;
        setHistLoads(res.data?.loads || []);
        const p = res.data?.pagination;
        if (p) setHistPagination(p);
      } catch {
        if (!cancelled) {
          setHistLoads([]);
          setHistPagination({ total: 0, page: 1, limit: histLimit, totalPages: 0 });
        }
      } finally {
        if (!cancelled) setHistLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showLoadHistory, activeTab, truck?.id, histPage, histClientId, histStartDate, histEndDate]);

  if (loading && !truck) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600 py-12 text-center">{t('common.loading')}</p>
      </div>
    );
  }

  if (!loading && !truck) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600 py-8 text-center">{error || t('common.error')}</p>
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => navigate('/transporteurs/trucks')}>
            {t('common.back')}
          </Button>
        </div>
      </div>
    );
  }

  const clientOptions = [
    { value: '', label: t('common.allClients'), searchText: '' },
    ...(Array.isArray(clients) ? clients : []).map((c) => ({
      value: c.id,
      label: c.companyName || c.id,
      searchText: [c.companyName, c.contactName, c.contactPhone, c.id].filter(Boolean).join(' ')
    }))
  ];

  const detailsContent = (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-3">{t('trucks.truckInformation')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">{t('trucks.plateNumber')}:</span>
            <span className="ml-2 font-medium">{truck.plateNumber}</span>
          </div>
          <div>
            <span className="text-gray-600">{t('trucks.driverName')}:</span>
            {(() => {
              const list =
                Array.isArray(truck.drivers) && truck.drivers.length > 0
                  ? truck.drivers
                  : (truck.assignedDrivers || []).map((dt) => dt.driver).filter(Boolean);
              if (list.length > 0) {
                return (
                  <div className="mt-1 space-y-1">
                    {list.map((d, index) => (
                      <div key={d.id || index} className="ml-2">
                        <span className="font-medium">
                          {d.firstName} {d.lastName}
                        </span>
                        {d.phone && <span className="text-gray-500 ml-2">• {d.phone}</span>}
                      </div>
                    ))}
                  </div>
                );
              }
              return (
                <span className="ml-2 text-gray-400 italic">{t('trucks.noDriversAssigned', 'No drivers assigned')}</span>
              );
            })()}
          </div>
          <div>
            <span className="text-gray-600">{t('trucks.clientsViaCarriers')}:</span>
            <span className="ml-2 font-medium">
              {truck.clients?.length ? truck.clients.map((c) => c.companyName).join(', ') : '—'}
            </span>
          </div>
          <div>
            <span className="text-gray-600">{t('trucks.transporteurs')}:</span>
            <span className="ml-2 font-medium">
              {Array.isArray(truck.transporteurs) && truck.transporteurs.length > 0
                ? truck.transporteurs.map((tr) => tr.companyName).join(', ')
                : '—'}
            </span>
          </div>
          <div>
            <span className="text-gray-600">{t('trucks.emptyWeight')}:</span>
            <span className="ml-2 font-medium">
              {truck.emptyWeight
                ? `${(truck.emptyWeight / 1000).toLocaleString('fr-FR', {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2
                  })} Ton`
                : 'N/A'}
            </span>
          </div>
          <div>
            <span className="text-gray-600">{t('trucks.status')}:</span>
            <span
              className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                truck.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {truck.status === 'active' ? t('trucks.active') : t('trucks.inactive')}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-3">{t('trucks.statistics')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600">{t('trucks.totalLoads')}:</span>
            <span className="ml-2 font-medium">{truck._count?.loads || 0}</span>
          </div>
          <div>
            <span className="text-gray-600">{t('trucks.deliveredLoads')}:</span>
            <span className="ml-2 font-medium">{truck._count?.deliveredLoads || 0}</span>
          </div>
          <div>
            <span className="text-gray-600">{t('trucks.created')}:</span>
            <span className="ml-2 font-medium">{formatDate(truck.createdAt)}</span>
          </div>
        </div>
      </div>

      {truck.clients?.length > 0 && (
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('trucks.clientsViaCarriers')}</h4>
          <div className="space-y-2 text-sm">
            {truck.clients.map((c) => (
              <div key={c.id}>
                <span className="font-medium">{c.companyName}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(truck.transporteurs) && truck.transporteurs.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('trucks.transporteurInformation')}</h4>
          <div className="space-y-4">
            {truck.transporteurs.map((tr) => (
              <div
                key={tr.id}
                className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm border-b border-blue-100 pb-3 last:border-0 last:pb-0"
              >
                <div>
                  <span className="text-gray-600">{t('trucks.company')}:</span>
                  <span className="ml-2 font-medium">{tr.companyName}</span>
                </div>
                <div>
                  <span className="text-gray-600">{t('trucks.contact')}:</span>
                  <span className="ml-2 font-medium">{tr.contactName}</span>
                </div>
                {tr.contactPhone && (
                  <div>
                    <span className="text-gray-600">{t('transporteurs.contactPhone')}:</span>
                    <span className="ml-2 font-medium">{tr.contactPhone}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/transporteurs/trucks')}
          className="p-2 hover:bg-gray-100 rounded-lg transition shrink-0"
          title={t('common.back')}
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <TruckIcon className="h-8 w-8 text-blue-600 shrink-0" />
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{truck.plateNumber}</h1>
          <p className="text-sm text-gray-600">{t('trucks.title')}</p>
        </div>
      </div>

      <div className="card">
        {showLoadHistory ? (
          <div className="space-y-4">
            <div className="flex border-b border-gray-200 gap-1 -mt-1">
              <button
                type="button"
                className={`px-3 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors ${
                  activeTab === 'info'
                    ? 'border-blue-600 text-blue-700 bg-white'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setActiveTab('info')}
              >
                {t('trucks.viewTabInfo')}
              </button>
              <button
                type="button"
                className={`px-3 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors ${
                  activeTab === 'history'
                    ? 'border-blue-600 text-blue-700 bg-white'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setActiveTab('history')}
              >
                {t('trucks.viewTabHistory')}
              </button>
            </div>

            {activeTab === 'info' && detailsContent}

            {activeTab === 'history' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                  <SearchableSelect
                    className="min-w-0"
                    label={t('loads.selectClient')}
                    name="histClientId"
                    value={histClientId}
                    onChange={(e) => setHistClientId(e.target.value)}
                    options={clientOptions}
                    placeholder={t('loads.selectClient')}
                  />
                  <Input
                    label={t('loads.startDate')}
                    type="date"
                    value={histStartDate}
                    onChange={(e) => setHistStartDate(e.target.value)}
                  />
                  <Input
                    label={t('loads.endDate')}
                    type="date"
                    value={histEndDate}
                    onChange={(e) => setHistEndDate(e.target.value)}
                  />
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setHistClientId('');
                        setHistStartDate('');
                        setHistEndDate('');
                        setHistPage(1);
                      }}
                    >
                      {t('common.clear')}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-gray-500">{t('trucks.historyScopeHint')}</p>

                {histLoading ? (
                  <p className="text-sm text-gray-600 py-6 text-center">{t('common.loading')}</p>
                ) : histLoads.length === 0 ? (
                  <p className="text-sm text-gray-600 py-6 text-center">{t('trucks.historyEmpty')}</p>
                ) : (
                  <>
                    <div className="overflow-x-auto border border-gray-200 rounded-md">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          <tr>
                            <th className="px-3 py-2">{t('loads.loadDate')}</th>
                            <th className="px-3 py-2">{t('loads.ticketNumber')}</th>
                            <th className="px-3 py-2">{t('loads.status')}</th>
                            <th className="px-3 py-2">{t('weighing.transactionType')}</th>
                            <th className="px-3 py-2">{t('trucks.client')}</th>
                            <th className="px-3 py-2">{t('trucks.historyDriver')}</th>
                            <th className="px-3 py-2">{t('weighing.weighbridge')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {histLoads.map((row) => {
                            const de = row.driverEntity;
                            const driverLabel = de
                              ? `${de.firstName || ''} ${de.lastName || ''}`.trim() || '—'
                              : '—';
                            return (
                              <tr key={row.id} className="hover:bg-gray-50/80">
                                <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                                  {row.loadDate ? formatDateTime(row.loadDate) : '—'}
                                </td>
                                <td className="px-3 py-2">
                                  <LoadTicketLink loadId={row.id} ticketNumber={row.ticket?.ticketNumber} />
                                </td>
                                <td className="px-3 py-2">
                                  {row.status ? t(`loads.statuses.${String(row.status).toLowerCase()}`) : '—'}
                                </td>
                                <td className="px-3 py-2">
                                  {row.transactionType === 'IMPORT'
                                    ? t('weighing.import')
                                    : row.transactionType === 'EXPORT'
                                      ? t('weighing.export')
                                      : row.transactionType || '—'}
                                </td>
                                <td className="px-3 py-2 text-gray-800">{row.client?.companyName || '—'}</td>
                                <td className="px-3 py-2 text-gray-800">{driverLabel}</td>
                                <td className="px-3 py-2 text-gray-700">{row.weighbridge?.name || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {histPagination.totalPages > 1 && (
                      <Pagination
                        total={histPagination.total}
                        page={histPagination.page}
                        limit={histPagination.limit}
                        totalPages={histPagination.totalPages}
                        onPageChange={(p) => setHistPage(p)}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          detailsContent
        )}
      </div>
    </div>
  );
};

const TruckDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) navigate('/transporteurs/trucks', { replace: true });
  }, [id, navigate]);

  if (!id) return null;

  return <TruckDetailContent key={id} id={id} />;
};

export default TruckDetailPage;
